import { Locale } from './translations';

type Section = {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
};

type FaqSection = {
  title: string;
  items: { q: string; a: string }[];
};

type LegalContent = {
  title: string;
  lastUpdatedLabel: string;
  sections: Section[];
};

type CookieContent = LegalContent;
type PrivacyContent = LegalContent;
type TermsContent = LegalContent;

type FaqContent = {
  title: string;
  subtitle: string;
  sections: FaqSection[];
};

export const legalContent: Record<
  Locale,
  {
    terms: TermsContent;
    privacy: PrivacyContent;
    cookies: CookieContent;
    faq: FaqContent;
  }
> = {
  en: {
    terms: {
      title: 'Terms of Service',
      lastUpdatedLabel: 'Last updated',
      sections: [
        {
          title: '1. Acceptance of Terms',
          paragraphs: [
            'By accessing and using byt.la, you agree to be bound by these Terms of Service.',
            'If you do not agree with these terms, please do not use the service.',
          ],
        },
        {
          title: '2. Description of Service',
          paragraphs: [
            'byt.la provides URL shortening services, analytics, QR code generation, and custom short codes.',
          ],
        },
        {
          title: '3. Acceptable Use',
          paragraphs: ['You agree not to use byt.la to:'],
          bullets: [
            'Shorten URLs that link to illegal content or activities',
            'Distribute malware, viruses, or harmful software',
            'Engage in phishing, scams, or fraudulent activities',
            'Harass, abuse, or harm others',
            'Violate applicable laws or regulations',
            'Infringe intellectual property rights',
            'Send spam or unsolicited messages',
          ],
        },
        {
          title: '4. Account Responsibilities',
          paragraphs: ['If you create an account, you are responsible for:'],
          bullets: [
            'Maintaining the security of your credentials',
            'All activities that occur under your account',
            'Notifying us of any unauthorized use',
          ],
        },
        {
          title: '5. Link Expiration',
          paragraphs: [
            'Links may expire based on settings you choose.',
            'We may remove inactive or expired links to maintain service quality.',
          ],
        },
        {
          title: '6. Service Availability',
          paragraphs: [
            'We strive to keep the service available but do not guarantee uninterrupted access.',
            'We may modify or discontinue features at any time.',
          ],
        },
        {
          title: '7. Intellectual Property',
          paragraphs: [
            'The byt.la service and its content are owned by us and protected by applicable laws.',
          ],
        },
        {
          title: '8. Disclaimer of Warranties',
          paragraphs: [
            'The service is provided "as is" without warranties of any kind.',
            'We do not guarantee that the service will be error-free or uninterrupted.',
          ],
        },
        {
          title: '9. Limitation of Liability',
          paragraphs: [
            'To the maximum extent permitted by law, we are not liable for indirect or consequential damages.',
          ],
        },
        {
          title: '10. Changes to Terms',
          paragraphs: [
            'We may update these terms periodically.',
            'Continued use after changes constitutes acceptance.',
          ],
        },
        {
          title: '11. Contact',
          paragraphs: ['If you have questions, please contact us through our support channels.'],
        },
      ],
    },
    privacy: {
      title: 'Privacy Policy',
      lastUpdatedLabel: 'Last updated',
      sections: [
        {
          title: '1. Information We Collect',
          paragraphs: ['We collect information to provide and improve our services.'],
          bullets: [
            'URLs you shorten and related metadata',
            'Account information (email, name, encrypted password)',
            'Analytics data (IP, referrer, user agent, language, timestamps)',
            'Device information derived from user agents',
            'Cookies for session and preferences',
          ],
        },
        {
          title: '2. How We Use Your Information',
          bullets: [
            'Provide, maintain, and improve the service',
            'Create and manage accounts',
            'Generate analytics and insights',
            'Detect abuse, bots, and fraud',
            'Respond to support requests',
            'Enforce our Terms of Service',
          ],
        },
        {
          title: '3. Sharing and Disclosure',
          paragraphs: ['We do not sell your personal information.'],
          bullets: [
            'With your consent',
            'When legally required',
            'To protect safety and rights',
            'With service providers under confidentiality obligations',
          ],
        },
        {
          title: '4. Data Security',
          bullets: [
            'Passwords are hashed using bcrypt',
            'Data is stored in secure databases',
            'HTTPS is used for data transmission',
            'Access is limited to authorized personnel',
          ],
        },
        {
          title: '5. Data Retention',
          bullets: [
            'Account data is retained until deletion',
            'Shortened URLs are retained until deleted or expired',
            'Analytics data is retained with the URL',
          ],
        },
        {
          title: '6. Cookies',
          paragraphs: [
            'We use cookies for authentication, preferences, and analytics.',
            'You can manage cookies in your browser settings.',
          ],
        },
        {
          title: '7. Your Rights',
          bullets: [
            'Access and update your data',
            'Delete your account and URLs',
            'Request data export',
            'Opt out of analytics via browser settings',
          ],
        },
        {
          title: '8. Third-Party Links',
          paragraphs: ['We are not responsible for the privacy practices of third-party sites.'],
        },
        {
          title: "9. Children's Privacy",
          paragraphs: ['Our service is not intended for children under 13.'],
        },
        {
          title: '10. International Transfers',
          paragraphs: ['Your data may be processed in other countries with different laws.'],
        },
        {
          title: '11. Changes to This Policy',
          paragraphs: ['We may update this policy and will update the "Last updated" date.'],
        },
        {
          title: '12. Contact',
          paragraphs: ['Contact us if you have questions about privacy.'],
        },
      ],
    },
    cookies: {
      title: 'Cookie Policy',
      lastUpdatedLabel: 'Last updated',
      sections: [
        {
          title: '1. What Are Cookies?',
          paragraphs: [
            'Cookies are small text files stored on your device to help websites work and improve experience.',
          ],
        },
        {
          title: '2. How We Use Cookies',
          bullets: [
            'Essential cookies for authentication and security',
            'Preference cookies (e.g., theme)',
            'Analytics cookies for usage insights',
            'Performance cookies for reliability',
          ],
        },
        {
          title: '3. Types of Cookies We Use',
          bullets: [
            'Session cookies (deleted when you close your browser)',
            'Persistent cookies (stored for a defined period)',
          ],
        },
        {
          title: '4. Third-Party Cookies',
          paragraphs: [
            'Some third-party services may set cookies. Review their policies for details.',
          ],
        },
        {
          title: '5. Managing Cookies',
          paragraphs: [
            'You can manage cookies through your browser settings. Disabling cookies may affect functionality.',
          ],
        },
        {
          title: '6. Do Not Track',
          paragraphs: ['We monitor industry standards for Do Not Track signals.'],
        },
        {
          title: '7. Updates',
          paragraphs: ['We may update this policy and will update the "Last updated" date.'],
        },
        {
          title: '8. Contact',
          paragraphs: ['Contact us if you have questions about cookies.'],
        },
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Find answers to common questions about using byt.la',
      sections: [
        {
          title: 'General Questions',
          items: [
            {
              q: 'What is byt.la?',
              a: 'byt.la is a URL shortener with analytics, QR codes, and custom short codes.',
            },
            {
              q: 'Is byt.la free to use?',
              a: 'Yes, byt.la is free to use with optional account features.',
            },
            {
              q: 'Do I need an account?',
              a: 'No, but an account unlocks dashboards, analytics, and bulk tools.',
            },
          ],
        },
        {
          title: 'Creating Short Links',
          items: [
            {
              q: 'How do I create a short URL?',
              a: 'Paste your long URL on the homepage and click "Shorten".',
            },
            {
              q: 'Can I customize my short code?',
              a: 'Yes, you can set a custom code if it is available.',
            },
            {
              q: 'Can I set an expiration date?',
              a: 'Yes, use the optional expiration setting when creating a link.',
            },
          ],
        },
        {
          title: 'Analytics & QR Codes',
          items: [
            {
              q: 'What analytics are available?',
              a: 'We provide clicks, referrers, timestamps, browsers, OS, and devices.',
            },
            {
              q: 'How do I generate a QR code?',
              a: 'Use the QR button on a link in your dashboard.',
            },
          ],
        },
        {
          title: 'Security & Privacy',
          items: [
            {
              q: 'Is my data secure?',
              a: 'We use HTTPS, secure password hashing, and access controls.',
            },
            {
              q: 'Do you sell my data?',
              a: 'No, we do not sell your personal information.',
            },
          ],
        },
      ],
    },
  },
  th: {
    terms: {
      title: 'ข้อกำหนดในการให้บริการ',
      lastUpdatedLabel: 'อัปเดตล่าสุด',
      sections: [
        {
          title: '1. การยอมรับข้อกำหนด',
          paragraphs: [
            'เมื่อคุณเข้าใช้งาน byt.la ถือว่าคุณยอมรับข้อกำหนดในการให้บริการนี้',
            'หากไม่ยอมรับ โปรดอย่าใช้งานบริการ',
          ],
        },
        {
          title: '2. รายละเอียดบริการ',
          paragraphs: ['byt.la ให้บริการย่อลิงก์ วิเคราะห์การใช้งาน สร้าง QR และกำหนดโค้ดสั้น'],
        },
        {
          title: '3. การใช้งานที่ยอมรับได้',
          paragraphs: ['คุณตกลงว่าจะไม่ใช้บริการเพื่อ:'],
          bullets: [
            'ย่อลิงก์ไปยังเนื้อหาที่ผิดกฎหมาย',
            'แจกจ่ายมัลแวร์ ไวรัส หรือซอฟต์แวร์อันตราย',
            'ทำฟิชชิง หลอกลวง หรือฉ้อโกง',
            'คุกคามหรือทำร้ายผู้อื่น',
            'ละเมิดกฎหมายที่เกี่ยวข้อง',
            'ละเมิดทรัพย์สินทางปัญญา',
            'ส่งสแปมหรือข้อความไม่พึงประสงค์',
          ],
        },
        {
          title: '4. ความรับผิดชอบของบัญชี',
          paragraphs: ['หากคุณสร้างบัญชี คุณต้องรับผิดชอบต่อ:'],
          bullets: [
            'การรักษาความปลอดภัยของข้อมูลเข้าสู่ระบบ',
            'กิจกรรมทั้งหมดในบัญชีของคุณ',
            'แจ้งเมื่อมีการใช้งานโดยไม่ได้รับอนุญาต',
          ],
        },
        {
          title: '5. การหมดอายุของลิงก์',
          paragraphs: ['ลิงก์อาจหมดอายุตามที่ตั้งค่าไว้', 'เราอาจลบลิงก์ที่หมดอายุหรือไม่ใช้งาน'],
        },
        {
          title: '6. ความพร้อมใช้งานของบริการ',
          paragraphs: [
            'เราไม่รับประกันว่าจะให้บริการได้อย่างต่อเนื่องตลอดเวลา',
            'เราอาจปรับปรุงหรือยุติคุณสมบัติบางอย่างได้',
          ],
        },
        {
          title: '7. ทรัพย์สินทางปัญญา',
          paragraphs: ['บริการ byt.la เป็นทรัพย์สินของเราและได้รับความคุ้มครองตามกฎหมาย'],
        },
        {
          title: '8. การปฏิเสธการรับประกัน',
          paragraphs: ['บริการให้ในสภาพ “ตามที่เป็น” โดยไม่มีการรับประกัน'],
        },
        {
          title: '9. การจำกัดความรับผิด',
          paragraphs: ['เราไม่รับผิดชอบต่อความเสียหายทางอ้อมหรือผลสืบเนื่องตามกฎหมายที่อนุญาต'],
        },
        {
          title: '10. การเปลี่ยนแปลงข้อกำหนด',
          paragraphs: ['เราอาจปรับปรุงข้อกำหนดเป็นครั้งคราว'],
        },
        {
          title: '11. ติดต่อเรา',
          paragraphs: ['หากมีคำถาม โปรดติดต่อผ่านช่องทางสนับสนุนของเรา'],
        },
      ],
    },
    privacy: {
      title: 'นโยบายความเป็นส่วนตัว',
      lastUpdatedLabel: 'อัปเดตล่าสุด',
      sections: [
        {
          title: '1. ข้อมูลที่เราเก็บรวบรวม',
          bullets: [
            'ลิงก์ที่คุณย่อและข้อมูลที่เกี่ยวข้อง',
            'ข้อมูลบัญชี (อีเมล ชื่อ รหัสผ่านที่เข้ารหัส)',
            'ข้อมูลวิเคราะห์ (IP แหล่งอ้างอิง user agent ภาษา เวลา)',
            'ข้อมูลอุปกรณ์จาก user agent',
            'คุกกี้เพื่อเข้าสู่ระบบและตั้งค่า',
          ],
        },
        {
          title: '2. การใช้ข้อมูล',
          bullets: [
            'ให้บริการและปรับปรุงระบบ',
            'จัดการบัญชีผู้ใช้',
            'จัดทำรายงานวิเคราะห์',
            'ตรวจจับการใช้งานที่ผิดปกติหรือบอต',
          ],
        },
        {
          title: '3. การเปิดเผยข้อมูล',
          paragraphs: ['เราไม่ขายข้อมูลส่วนบุคคลของคุณ'],
          bullets: [
            'ตามความยินยอมของคุณ',
            'ตามข้อกำหนดทางกฎหมาย',
            'เพื่อปกป้องสิทธิ์และความปลอดภัย',
            'กับผู้ให้บริการที่มีข้อผูกพันด้านความลับ',
          ],
        },
        {
          title: '4. ความปลอดภัยของข้อมูล',
          bullets: [
            'รหัสผ่านถูกแฮชด้วย bcrypt',
            'ส่งข้อมูลผ่าน HTTPS',
            'จำกัดการเข้าถึงเฉพาะผู้มีสิทธิ์',
          ],
        },
        {
          title: '5. ระยะเวลาการเก็บรักษา',
          bullets: [
            'เก็บข้อมูลบัญชีจนกว่าจะลบ',
            'ลิงก์เก็บจนกว่าจะลบหรือหมดอายุ',
            'ข้อมูลวิเคราะห์เก็บตามลิงก์นั้น',
          ],
        },
        {
          title: '6. คุกกี้',
          paragraphs: ['คุณสามารถจัดการคุกกี้ผ่านการตั้งค่าเบราว์เซอร์'],
        },
        {
          title: '7. สิทธิ์ของคุณ',
          bullets: ['เข้าถึงและแก้ไขข้อมูล', 'ลบบัญชีและลิงก์', 'ขอส่งออกข้อมูล'],
        },
        {
          title: '8. ลิงก์ของบุคคลที่สาม',
          paragraphs: ['เราไม่รับผิดชอบต่อแนวทางความเป็นส่วนตัวของเว็บไซต์ภายนอก'],
        },
        {
          title: '9. ความเป็นส่วนตัวของเด็ก',
          paragraphs: ['บริการไม่เหมาะสำหรับเด็กอายุต่ำกว่า 13 ปี'],
        },
        {
          title: '10. การโอนข้อมูลระหว่างประเทศ',
          paragraphs: ['ข้อมูลอาจถูกประมวลผลในประเทศอื่น'],
        },
        {
          title: '11. การเปลี่ยนแปลงนโยบาย',
          paragraphs: ['เราจะอัปเดตวันที่เมื่อมีการเปลี่ยนแปลง'],
        },
        {
          title: '12. ติดต่อเรา',
          paragraphs: ['ติดต่อเราเมื่อมีคำถามเกี่ยวกับความเป็นส่วนตัว'],
        },
      ],
    },
    cookies: {
      title: 'นโยบายคุกกี้',
      lastUpdatedLabel: 'อัปเดตล่าสุด',
      sections: [
        {
          title: '1. คุกกี้คืออะไร',
          paragraphs: ['คุกกี้คือไฟล์ข้อความขนาดเล็กที่เก็บบนอุปกรณ์ของคุณ'],
        },
        {
          title: '2. เราใช้คุกกี้อย่างไร',
          bullets: [
            'คุกกี้จำเป็นเพื่อความปลอดภัยและการยืนยันตัวตน',
            'คุกกี้การตั้งค่า (เช่น ธีม)',
            'คุกกี้วิเคราะห์การใช้งาน',
          ],
        },
        {
          title: '3. ประเภทคุกกี้',
          bullets: ['คุกกี้แบบชั่วคราว', 'คุกกี้แบบถาวร'],
        },
        {
          title: '4. คุกกี้จากบุคคลที่สาม',
          paragraphs: ['บางบริการภายนอกอาจตั้งคุกกี้ของตนเอง'],
        },
        {
          title: '5. การจัดการคุกกี้',
          paragraphs: ['คุณสามารถจัดการคุกกี้ผ่านการตั้งค่าเบราว์เซอร์'],
        },
        {
          title: '6. Do Not Track',
          paragraphs: ['เราติดตามมาตรฐานที่เกี่ยวข้องกับ DNT อย่างใกล้ชิด'],
        },
        {
          title: '7. การอัปเดตนโยบาย',
          paragraphs: ['เราจะอัปเดตวันที่เมื่อมีการเปลี่ยนแปลง'],
        },
        {
          title: '8. ติดต่อเรา',
          paragraphs: ['ติดต่อเราเมื่อมีคำถามเกี่ยวกับคุกกี้'],
        },
      ],
    },
    faq: {
      title: 'คำถามที่พบบ่อย',
      subtitle: 'ค้นหาคำตอบสำหรับการใช้งาน byt.la',
      sections: [
        {
          title: 'คำถามทั่วไป',
          items: [
            {
              q: 'byt.la คืออะไร?',
              a: 'บริการย่อลิงก์พร้อมสถิติและ QR code',
            },
            {
              q: 'ใช้งานฟรีหรือไม่?',
              a: 'ใช้งานฟรี พร้อมตัวเลือกบัญชีเพื่อฟีเจอร์เพิ่มเติม',
            },
            {
              q: 'ต้องมีบัญชีหรือไม่?',
              a: 'ไม่จำเป็น แต่บัญชีช่วยให้ดูแดชบอร์ดและใช้งานแบบ bulk ได้',
            },
          ],
        },
        {
          title: 'การสร้างลิงก์',
          items: [
            {
              q: 'สร้างลิงก์สั้นอย่างไร?',
              a: 'วางลิงก์บนหน้าแรกแล้วกด “Shorten”',
            },
            {
              q: 'กำหนดโค้ดเองได้ไหม?',
              a: 'ได้ หากโค้ดนั้นว่าง',
            },
            {
              q: 'ตั้งวันหมดอายุได้ไหม?',
              a: 'ได้ เลือกเวลาหมดอายุขณะสร้างลิงก์',
            },
          ],
        },
        {
          title: 'สถิติและ QR',
          items: [
            {
              q: 'มีสถิติอะไรบ้าง?',
              a: 'จำนวนคลิก แหล่งอ้างอิง เบราว์เซอร์ ระบบ และอุปกรณ์',
            },
            {
              q: 'สร้าง QR อย่างไร?',
              a: 'กดปุ่ม QR จากลิงก์ในแดชบอร์ด',
            },
          ],
        },
        {
          title: 'ความปลอดภัยและความเป็นส่วนตัว',
          items: [
            {
              q: 'ข้อมูลปลอดภัยหรือไม่?',
              a: 'เราใช้ HTTPS และการเข้ารหัสรหัสผ่าน',
            },
            {
              q: 'ขายข้อมูลหรือไม่?',
              a: 'ไม่ขายข้อมูลส่วนบุคคล',
            },
          ],
        },
      ],
    },
  },
  id: {
    terms: {
      title: 'Ketentuan Layanan',
      lastUpdatedLabel: 'Terakhir diperbarui',
      sections: [
        {
          title: '1. Penerimaan Ketentuan',
          paragraphs: [
            'Dengan menggunakan byt.la, Anda setuju dengan ketentuan ini.',
            'Jika tidak setuju, jangan gunakan layanan ini.',
          ],
        },
        {
          title: '2. Deskripsi Layanan',
          paragraphs: ['byt.la menyediakan pemendek URL, analitik, pembuatan QR, dan kode kustom.'],
        },
        {
          title: '3. Penggunaan yang Dilarang',
          paragraphs: ['Anda setuju untuk tidak:'],
          bullets: [
            'Menautkan ke konten ilegal',
            'Menyebarkan malware atau virus',
            'Melakukan phishing atau penipuan',
            'Melecehkan atau merugikan orang lain',
            'Melanggar hukum yang berlaku',
            'Melanggar hak kekayaan intelektual',
            'Mengirim spam',
          ],
        },
        {
          title: '4. Tanggung Jawab Akun',
          bullets: [
            'Menjaga keamanan kredensial',
            'Bertanggung jawab atas aktivitas akun',
            'Memberi tahu jika ada penggunaan tidak sah',
          ],
        },
        {
          title: '5. Kedaluwarsa Tautan',
          paragraphs: ['Tautan dapat kedaluwarsa sesuai pengaturan Anda.'],
        },
        {
          title: '6. Ketersediaan Layanan',
          paragraphs: ['Kami tidak menjamin layanan selalu tersedia tanpa gangguan.'],
        },
        {
          title: '7. Hak Kekayaan Intelektual',
          paragraphs: ['Layanan byt.la dilindungi oleh hukum yang berlaku.'],
        },
        {
          title: '8. Penyangkalan Garansi',
          paragraphs: ['Layanan disediakan “apa adanya” tanpa jaminan.'],
        },
        {
          title: '9. Batasan Tanggung Jawab',
          paragraphs: ['Kami tidak bertanggung jawab atas kerugian tidak langsung.'],
        },
        {
          title: '10. Perubahan Ketentuan',
          paragraphs: ['Ketentuan dapat diperbarui dari waktu ke waktu.'],
        },
        {
          title: '11. Kontak',
          paragraphs: ['Hubungi kami jika ada pertanyaan.'],
        },
      ],
    },
    privacy: {
      title: 'Kebijakan Privasi',
      lastUpdatedLabel: 'Terakhir diperbarui',
      sections: [
        {
          title: '1. Informasi yang Dikumpulkan',
          bullets: [
            'URL yang dipendekkan',
            'Data akun (email, nama, kata sandi terenkripsi)',
            'Data analitik (IP, referrer, user agent)',
            'Informasi perangkat',
            'Cookie untuk sesi dan preferensi',
          ],
        },
        {
          title: '2. Penggunaan Informasi',
          bullets: [
            'Memberikan dan meningkatkan layanan',
            'Mengelola akun',
            'Menyediakan analitik',
            'Mendeteksi penyalahgunaan',
          ],
        },
        {
          title: '3. Berbagi Informasi',
          paragraphs: ['Kami tidak menjual data Anda.'],
        },
        {
          title: '4. Keamanan Data',
          bullets: ['HTTPS digunakan', 'Kata sandi di-hash dengan bcrypt'],
        },
        {
          title: '5. Retensi Data',
          bullets: ['Data disimpan selama akun atau tautan aktif'],
        },
        {
          title: '6. Cookie',
          paragraphs: ['Cookie membantu autentikasi dan preferensi.'],
        },
        {
          title: '7. Hak Anda',
          bullets: ['Akses, perbarui, atau hapus data Anda'],
        },
        {
          title: '8. Tautan Pihak Ketiga',
          paragraphs: ['Kami tidak bertanggung jawab atas situs pihak ketiga.'],
        },
        {
          title: '9. Privasi Anak',
          paragraphs: ['Layanan tidak ditujukan untuk anak di bawah 13 tahun.'],
        },
        {
          title: '10. Transfer Internasional',
          paragraphs: ['Data dapat diproses di negara lain.'],
        },
        {
          title: '11. Perubahan Kebijakan',
          paragraphs: ['Kami akan memperbarui tanggal jika ada perubahan.'],
        },
        {
          title: '12. Kontak',
          paragraphs: ['Hubungi kami untuk pertanyaan privasi.'],
        },
      ],
    },
    cookies: {
      title: 'Kebijakan Cookie',
      lastUpdatedLabel: 'Terakhir diperbarui',
      sections: [
        {
          title: '1. Apa itu Cookie?',
          paragraphs: ['Cookie adalah file kecil yang disimpan di perangkat Anda.'],
        },
        {
          title: '2. Cara Kami Menggunakan Cookie',
          bullets: ['Cookie esensial untuk autentikasi', 'Cookie preferensi', 'Cookie analitik'],
        },
        {
          title: '3. Jenis Cookie',
          bullets: ['Session cookie', 'Persistent cookie'],
        },
        {
          title: '4. Cookie Pihak Ketiga',
          paragraphs: ['Beberapa layanan pihak ketiga dapat menyetel cookie.'],
        },
        {
          title: '5. Mengelola Cookie',
          paragraphs: ['Anda dapat mengatur cookie di browser.'],
        },
        {
          title: '6. Do Not Track',
          paragraphs: ['Kami mengikuti perkembangan standar DNT.'],
        },
        {
          title: '7. Pembaruan',
          paragraphs: ['Kebijakan ini dapat diperbarui.'],
        },
        {
          title: '8. Kontak',
          paragraphs: ['Hubungi kami jika ada pertanyaan tentang cookie.'],
        },
      ],
    },
    faq: {
      title: 'Pertanyaan yang Sering Diajukan',
      subtitle: 'Temukan jawaban tentang penggunaan byt.la',
      sections: [
        {
          title: 'Pertanyaan Umum',
          items: [
            { q: 'Apa itu byt.la?', a: 'Pemendek URL dengan analitik dan QR.' },
            { q: 'Apakah gratis?', a: 'Ya, gratis dengan fitur akun opsional.' },
            { q: 'Perlu akun?', a: 'Tidak, tetapi akun memberi fitur tambahan.' },
          ],
        },
        {
          title: 'Membuat Tautan',
          items: [
            { q: 'Bagaimana membuat tautan pendek?', a: 'Tempel URL di beranda.' },
            { q: 'Bisa kustom?', a: 'Ya, jika tersedia.' },
            { q: 'Bisa kedaluwarsa?', a: 'Ya, atur saat membuat.' },
          ],
        },
        {
          title: 'Analitik & QR',
          items: [
            { q: 'Analitik apa saja?', a: 'Klik, referrer, browser, OS, perangkat.' },
            { q: 'Bagaimana membuat QR?', a: 'Gunakan tombol QR di dashboard.' },
          ],
        },
        {
          title: 'Keamanan & Privasi',
          items: [
            { q: 'Apakah data aman?', a: 'Kami memakai HTTPS dan hash password.' },
            { q: 'Apakah data dijual?', a: 'Tidak.' },
          ],
        },
      ],
    },
  },
};
